import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BriefcaseBusiness,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Plane,
  Save,
  Shield,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { useAuth } from "./Auth";

const WEEKDAY_OPTIONS = [
  { value: 0, short: "L", label: "Lundi" },
  { value: 1, short: "M", label: "Mardi" },
  { value: 2, short: "M", label: "Mercredi" },
  { value: 3, short: "J", label: "Jeudi" },
  { value: 4, short: "V", label: "Vendredi" },
  { value: 5, short: "S", label: "Samedi" },
  { value: 6, short: "D", label: "Dimanche" },
];

const CONTRACT_OPTIONS = [
  { value: "cdi", label: "CDI" },
  { value: "cdd", label: "CDD" },
  { value: "stage", label: "Stage" },
  { value: "alternance", label: "Alternance" },
  { value: "freelance", label: "Freelance" },
  { value: "other", label: "Autre" },
];

const TIME_OFF_OPTIONS = [
  { value: "vacation", label: "Conges" },
  { value: "sick", label: "Maladie" },
  { value: "training", label: "Formation" },
  { value: "personal", label: "Perso" },
  { value: "other", label: "Autre" },
];

const EMPTY_EMPLOYEE_FORM = {
  name: "",
  email: "",
  production_rate: 85,
  weekly_hours: 35,
  is_active: true,
  is_admin: false,
  can_use_guttenberg: false,
  can_edit_offers: false,
  can_use_home: false,
  can_view_pilotage: false,
  can_use_new_project: false,
  arrival_date: "",
  departure_date: "",
  contract_type: "cdi",
  weekly_work_days: [0, 1, 2, 3, 4],
};

const EMPTY_TIME_OFF_FORM = {
  id: null,
  start_date: "",
  end_date: "",
  leave_type: "vacation",
  label: "",
};

const apiBaseUrl =
  process.env.NODE_ENV === "development"
    ? `${process.env.REACT_APP_API_URL || "http://localhost:8000"}`
    : `${process.env.REACT_APP_API_URL}/api` || "http://localhost:8000";

const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const parts = String(value).split("-");
  if (parts.length !== 3) return null;
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
};

const toDateInput = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = parseDate(value);
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDate = (value) => {
  const date = parseDate(value);
  return date ? date.toLocaleDateString("fr-FR") : "Non renseigne";
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const DAY_IN_MS = 1000 * 60 * 60 * 24;

const startOfMonth = (year, monthIndex) => new Date(year, monthIndex, 1);

const endOfMonth = (year, monthIndex) => new Date(year, monthIndex + 1, 0);

const startOfWeek = (date) => {
  const current = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayIndex = current.getDay();
  const diff = dayIndex === 0 ? -6 : 1 - dayIndex;
  return addDays(current, diff);
};

const getDateKey = (value) => toDateInput(value);

const clampDate = (date, min, max) => {
  if (date < min) return new Date(min);
  if (date > max) return new Date(max);
  return new Date(date);
};

const normalizeRange = (start, end) => {
  if (!start || !end) return null;
  return start <= end ? { start, end } : { start: end, end: start };
};

const rangesOverlap = (startA, endA, startB, endB) => startA <= endB && endA >= startB;

const countInclusiveDays = (start, end) =>
  Math.max(1, Math.round((end - start) / DAY_IN_MS) + 1);

const maxDate = (left, right) => (left > right ? left : right);

const minDate = (left, right) => (left < right ? left : right);

const getEmploymentStatus = (employee, referenceDate = new Date()) => {
  const arrival = parseDate(employee?.arrival_date);
  const departure = parseDate(employee?.departure_date);
  if (arrival && arrival > referenceDate) return "future";
  if ((departure && departure < referenceDate) || employee?.is_active === false) return "past";
  return "current";
};

const percentBetweenDates = (date, start, totalDays) => {
  const diff = Math.round((date - start) / DAY_IN_MS);
  return (Math.max(0, diff) / totalDays) * 100;
};

const sortNumeric = (values = []) => [...values].map(Number).sort((left, right) => left - right);

const weekdayLabel = (days = []) => {
  const sorted = sortNumeric(days);
  if (!sorted.length) return "Aucun jour";
  return sorted
    .map((day) => WEEKDAY_OPTIONS.find((entry) => entry.value === day)?.short || "?")
    .join(" ");
};

const formatWeeklyHours = (value) => {
  const hours = Number(value ?? 35);
  if (!Number.isFinite(hours) || hours <= 0) return "35h";
  return `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(hours)}h`;
};

const employeeWorksOnDay = (employee, day) => {
  const workingDays =
    Array.isArray(employee?.weekly_work_days) && employee.weekly_work_days.length
      ? employee.weekly_work_days.map(Number)
      : [0, 1, 2, 3, 4];
  return workingDays.includes(day.getDay() === 0 ? 6 : day.getDay() - 1);
};

const employeeIsEmployedOnDay = (employee, day) => {
  if (!employee || employee.is_active === false) return false;
  const arrival = parseDate(employee.arrival_date);
  const departure = parseDate(employee.departure_date);
  if (arrival && day < arrival) return false;
  if (departure && day > departure) return false;
  return true;
};

const employeeTimeOffsOnDay = (employee, day) =>
  (employee?.time_offs || []).filter((timeOff) => {
    const start = parseDate(timeOff.start_date);
    const end = parseDate(timeOff.end_date);
    return start && end && day >= start && day <= end;
  });

const getWorkingDaySegments = (employee, rangeStart, rangeEnd) => {
  const segments = [];
  let cursor = new Date(rangeStart);
  let segmentStart = null;

  while (cursor <= rangeEnd) {
    const currentDay = new Date(cursor);
    const isWorkingDay =
      employeeIsEmployedOnDay(employee, currentDay) && employeeWorksOnDay(employee, currentDay);

    if (isWorkingDay && !segmentStart) {
      segmentStart = currentDay;
    }

    if ((!isWorkingDay || getDateKey(currentDay) === getDateKey(rangeEnd)) && segmentStart) {
      segments.push({
        start: segmentStart,
        end: isWorkingDay ? currentDay : addDays(currentDay, -1),
      });
      segmentStart = null;
    }

    cursor = addDays(cursor, 1);
  }

  return segments;
};

const toEmployeeForm = (employee) => ({
  name: employee?.name || "",
  email: employee?.email || "",
  production_rate: Number(employee?.production_rate ?? 85),
  weekly_hours: Number(employee?.weekly_hours ?? 35),
  is_active: employee?.is_active !== false,
  is_admin: Boolean(employee?.is_admin),
  can_use_guttenberg: Boolean(employee?.can_use_guttenberg),
  can_edit_offers: Boolean(employee?.can_edit_offers),
  can_use_home: Boolean(employee?.can_use_home),
  can_view_pilotage: Boolean(employee?.can_view_pilotage),
  can_use_new_project: Boolean(employee?.can_use_new_project),
  arrival_date: toDateInput(employee?.arrival_date),
  departure_date: toDateInput(employee?.departure_date),
  contract_type: employee?.contract_type || "cdi",
  weekly_work_days:
    Array.isArray(employee?.weekly_work_days) && employee.weekly_work_days.length
      ? sortNumeric(employee.weekly_work_days)
      : [0, 1, 2, 3, 4],
});

const timeOffTypeLabel = (value) =>
  TIME_OFF_OPTIONS.find((option) => option.value === value)?.label || "Autre";

const contractTypeLabel = (value) =>
  CONTRACT_OPTIONS.find((option) => option.value === value)?.label || "Autre";

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [editorMode, setEditorMode] = useState("edit");
  const [employeeForm, setEmployeeForm] = useState({
    ...EMPTY_EMPLOYEE_FORM,
    arrival_date: toDateInput(new Date()),
  });
  const [timeOffForm, setTimeOffForm] = useState(EMPTY_TIME_OFF_FORM);
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedRange, setSelectedRange] = useState(() => {
    const today = new Date();
    return {
      start: today,
      end: today,
    };
  });
  const [viewportRange, setViewportRange] = useState(null);
  const { getAuthHeaders, updateUser, user } = useAuth();
  const roadmapRef = useRef(null);
  const roadmapLabelsRef = useRef(null);
  const roadmapOverviewRef = useRef(null);
  const roadmapScrollSyncRef = useRef(false);
  const rangeDragRef = useRef(null);
  const viewportDragRef = useRef(null);

  const isSuperAdmin = Number(user?.employee_id ?? user?.id) === 1;

  const request = useCallback(
    async (endpoint, options = {}) => {
      const response = await fetch(`${apiBaseUrl}${endpoint}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
          ...(options.headers || {}),
        },
      });
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.detail || "Une erreur est survenue");
      }
      return response.status === 204 ? null : response.json();
    },
    [getAuthHeaders]
  );

  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();
      const data = await request("/employees", { method: "GET" });
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      setEmployees([]);
      setError(err.message || "Echec du chargement des employes");
    } finally {
      setLoading(false);
    }
  }, [clearMessages, request]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const selectedEmployee = useMemo(
    () => employees.find((employee) => String(employee.id) === String(selectedEmployeeId)) || null,
    [employees, selectedEmployeeId]
  );

  useEffect(() => {
    // Automatic selection must not replace a new employee draft.
    if (loading || editorMode === "create") return;
    if (!employees.length) {
      setSelectedEmployeeId(null);
      setEditorMode("create");
      setEmployeeForm({
        ...EMPTY_EMPLOYEE_FORM,
        arrival_date: toDateInput(new Date()),
      });
      return;
    }
    const stillExists = employees.some(
      (employee) => String(employee.id) === String(selectedEmployeeId)
    );
    if (!stillExists) {
      const firstEmployee = employees[0];
      setSelectedEmployeeId(firstEmployee.id);
      setEditorMode("edit");
      setEmployeeForm(toEmployeeForm(firstEmployee));
    }
  }, [employees, selectedEmployeeId, editorMode, loading]);

  useEffect(() => {
    if (editorMode === "edit" && selectedEmployee) {
      setEmployeeForm(toEmployeeForm(selectedEmployee));
      setTimeOffForm(EMPTY_TIME_OFF_FORM);
    }
  }, [editorMode, selectedEmployee]);

  const openCreateEmployee = () => {
    clearMessages();
    setEditorMode("create");
    setSelectedEmployeeId(null);
    setEmployeeForm({
      ...EMPTY_EMPLOYEE_FORM,
      arrival_date: toDateInput(new Date()),
    });
    setTimeOffForm(EMPTY_TIME_OFF_FORM);
  };

  const selectEmployee = (employee) => {
    clearMessages();
    setEditorMode("edit");
    setSelectedEmployeeId(employee.id);
    setEmployeeForm(toEmployeeForm(employee));
    setTimeOffForm(EMPTY_TIME_OFF_FORM);
  };

  const updateEmployeeForm = (field, value) => {
    setEmployeeForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleWorkingDay = (day) => {
    setEmployeeForm((prev) => {
      const current = new Set((prev.weekly_work_days || []).map(Number));
      if (current.has(day)) {
        current.delete(day);
      } else {
        current.add(day);
      }
      return {
        ...prev,
        weekly_work_days: Array.from(current).sort((left, right) => left - right),
      };
    });
  };

  const normalizeEmployeePayload = () => ({
    name: String(employeeForm.name || "").trim(),
    email: String(employeeForm.email || "").trim(),
    production_rate: Math.max(0, Math.min(100, Number(employeeForm.production_rate || 0))),
    weekly_hours: Math.max(1, Math.min(80, Number(employeeForm.weekly_hours || 35))),
    is_active: Boolean(employeeForm.is_active),
    is_admin: Boolean(employeeForm.is_admin),
    can_use_guttenberg: Boolean(employeeForm.can_use_guttenberg),
    can_edit_offers: Boolean(employeeForm.can_edit_offers),
    can_use_home: Boolean(employeeForm.can_use_home),
    can_view_pilotage: Boolean(employeeForm.can_view_pilotage),
    can_use_new_project: Boolean(employeeForm.can_use_new_project),
    arrival_date: employeeForm.arrival_date || toDateInput(new Date()),
    departure_date: employeeForm.departure_date || null,
    contract_type: employeeForm.contract_type || "cdi",
    weekly_work_days: sortNumeric(employeeForm.weekly_work_days),
  });

  const handleEmployeeSubmit = async (event) => {
    event.preventDefault();
    try {
      clearMessages();
      const payload = normalizeEmployeePayload();
      if (!payload.name || !payload.email) {
        throw new Error("Le nom et l'email sont requis");
      }
      const endpoint =
        editorMode === "create"
          ? "/employees"
          : `/employees/${selectedEmployee?.id}`;
      const method = editorMode === "create" ? "POST" : "PATCH";
      const saved = await request(endpoint, {
        method,
        body: JSON.stringify(payload),
      });
      const currentEmployeeId = user?.employee_id ?? user?.id;
      if (String(saved?.id) === String(currentEmployeeId)) {
        updateUser({
          is_admin: Boolean(saved?.is_admin),
          can_use_guttenberg: Boolean(saved?.can_use_guttenberg),
          can_edit_offers: Boolean(saved?.can_edit_offers),
          can_use_home: Boolean(saved?.can_use_home),
          can_view_pilotage: Boolean(saved?.can_view_pilotage),
          can_use_new_project: Boolean(saved?.can_use_new_project),
        });
      }
      await fetchEmployees();
      setEditorMode("edit");
      setSelectedEmployeeId(saved?.id || selectedEmployee?.id || null);
      setSuccess(editorMode === "create" ? "Employe ajoute" : "Employe mis a jour");
    } catch (err) {
      setError(err.message || "Echec de l'enregistrement");
    }
  };

  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;
    const confirmed = window.confirm(`Supprimer ${selectedEmployee.name} ?`);
    if (!confirmed) return;
    try {
      clearMessages();
      await request(`/employees/${selectedEmployee.id}`, { method: "DELETE" });
      await fetchEmployees();
      setSuccess("Employe supprime");
    } catch (err) {
      setError(err.message || "Echec de la suppression");
    }
  };

  const handleTimeOffSubmit = async (event) => {
    event.preventDefault();
    if (!selectedEmployee) return;
    try {
      clearMessages();
      if (!timeOffForm.start_date || !timeOffForm.end_date) {
        throw new Error("Les dates de debut et de fin sont requises");
      }
      const endpoint = timeOffForm.id
        ? `/employees/${selectedEmployee.id}/time-offs/${timeOffForm.id}`
        : `/employees/${selectedEmployee.id}/time-offs`;
      const method = timeOffForm.id ? "PATCH" : "POST";
      await request(endpoint, {
        method,
        body: JSON.stringify({
          start_date: timeOffForm.start_date,
          end_date: timeOffForm.end_date,
          leave_type: timeOffForm.leave_type,
          label: String(timeOffForm.label || "").trim() || null,
        }),
      });
      await fetchEmployees();
      setSelectedEmployeeId(selectedEmployee.id);
      setTimeOffForm(EMPTY_TIME_OFF_FORM);
      setSuccess(timeOffForm.id ? "Absence mise a jour" : "Absence ajoutee");
    } catch (err) {
      setError(err.message || "Echec de l'enregistrement de l'absence");
    }
  };

  const editTimeOff = (timeOff) => {
    setTimeOffForm({
      id: timeOff.id,
      start_date: toDateInput(timeOff.start_date),
      end_date: toDateInput(timeOff.end_date),
      leave_type: timeOff.leave_type || "vacation",
      label: timeOff.label || "",
    });
  };

  const deleteTimeOff = async (timeOffId) => {
    if (!selectedEmployee) return;
    try {
      clearMessages();
      await request(`/employees/${selectedEmployee.id}/time-offs/${timeOffId}`, {
        method: "DELETE",
      });
      await fetchEmployees();
      setSelectedEmployeeId(selectedEmployee.id);
      if (String(timeOffForm.id) === String(timeOffId)) {
        setTimeOffForm(EMPTY_TIME_OFF_FORM);
      }
      setSuccess("Absence supprimee");
    } catch (err) {
      setError(err.message || "Echec de la suppression de l'absence");
    }
  };

  const dayStatsByKey = useMemo(() => {
    const map = new Map();
    for (let monthIndex = 0; monthIndex < 12; monthIndex += 1) {
      let cursor = startOfMonth(year, monthIndex);
      const monthEnd = endOfMonth(year, monthIndex);
      while (cursor <= monthEnd) {
        const currentDay = new Date(cursor);
        const dateKey = getDateKey(currentDay);
        const arrivals = [];
        const departures = [];
        const scheduled = [];
        const available = [];
        const timeOffs = [];
        employees.forEach((employee) => {
          if (getDateKey(employee.arrival_date) === dateKey) {
            arrivals.push(employee);
          }
          if (employee.departure_date && getDateKey(employee.departure_date) === dateKey) {
            departures.push(employee);
          }
          if (!employeeIsEmployedOnDay(employee, currentDay)) return;
          if (!employeeWorksOnDay(employee, currentDay)) return;
          scheduled.push(employee);
          const activeTimeOffs = employeeTimeOffsOnDay(employee, currentDay);
          if (activeTimeOffs.length) {
            timeOffs.push({ employee, timeOffs: activeTimeOffs });
            return;
          }
          available.push(employee);
        });
        map.set(dateKey, {
          date: currentDay,
          arrivals,
          departures,
          scheduled,
          available,
          timeOffs,
        });
        cursor = addDays(cursor, 1);
      }
    }
    return map;
  }, [employees, year]);
  const todayStats = dayStatsByKey.get(getDateKey(new Date())) || null;
  const yearStart = useMemo(() => new Date(year, 0, 1), [year]);
  const yearEnd = useMemo(() => new Date(year, 11, 31), [year]);
  const totalYearDays = useMemo(
    () => countInclusiveDays(yearStart, yearEnd),
    [yearEnd, yearStart]
  );

  useEffect(() => {
    setSelectedRange((prev) => {
      const currentStart = clampDate(parseDate(prev?.start) || new Date(), yearStart, yearEnd);
      const currentEnd = clampDate(parseDate(prev?.end) || currentStart, yearStart, yearEnd);
      return normalizeRange(currentStart, currentEnd);
    });
    setViewportRange((prev) => {
      const currentStart = clampDate(parseDate(prev?.start) || yearStart, yearStart, yearEnd);
      const currentEnd = clampDate(parseDate(prev?.end) || yearEnd, yearStart, yearEnd);
      return normalizeRange(currentStart, currentEnd);
    });
  }, [yearEnd, yearStart]);

  const displayedRange = useMemo(() => {
    const start = clampDate(parseDate(viewportRange?.start) || yearStart, yearStart, yearEnd);
    const end = clampDate(parseDate(viewportRange?.end) || yearEnd, yearStart, yearEnd);
    return normalizeRange(start, end);
  }, [viewportRange, yearEnd, yearStart]);

  const viewStart = displayedRange?.start || yearStart;
  const viewEnd = displayedRange?.end || yearEnd;
  const totalViewDays = useMemo(
    () => countInclusiveDays(viewStart, viewEnd),
    [viewEnd, viewStart]
  );

  const summary = useMemo(() => {
    const activeRoster = employees.filter((employee) => employee.is_active !== false).length;
    const upcomingDepartures = employees.filter((employee) => {
      const departure = parseDate(employee.departure_date);
      return departure && departure >= new Date();
    }).length;
    const openTimeOffs = employees.reduce(
      (total, employee) => total + (employee.time_offs || []).length,
      0
    );
    return {
      activeRoster,
      availableToday: todayStats?.available?.length || 0,
      scheduledToday: todayStats?.scheduled?.length || 0,
      upcomingDepartures,
      openTimeOffs,
    };
  }, [employees, todayStats]);

  const employeesWithHighlights = useMemo(
    () =>
      employees.map((employee) => {
        const nextTimeOff = (employee.time_offs || [])
          .slice()
          .sort((left, right) =>
            String(left.start_date).localeCompare(String(right.start_date))
          )
          .find((timeOff) => {
            const end = parseDate(timeOff.end_date);
            return end && end >= new Date();
          });
        return { ...employee, nextTimeOff };
      }),
    [employees]
  );

  const employeeSections = useMemo(() => {
    const groups = {
      current: [],
      future: [],
      past: [],
    };
    employeesWithHighlights.forEach((employee) => {
      groups[getEmploymentStatus(employee)].push(employee);
    });
    const sortEmployees = (list) =>
      list
        .slice()
        .sort((left, right) => String(left.name || "").localeCompare(String(right.name || "")));
    return [
      { key: "current", label: "En poste", items: sortEmployees(groups.current) },
      { key: "future", label: "Arrivees a venir", items: sortEmployees(groups.future) },
      { key: "past", label: "Sortis", items: sortEmployees(groups.past) },
    ].filter((section) => section.items.length > 0);
  }, [employeesWithHighlights]);

  const roadmapRows = useMemo(() => {
    const rows = employees
      .filter((employee) => {
        const arrival = parseDate(employee.arrival_date);
        const departure = parseDate(employee.departure_date) || yearEnd;
        return arrival && rangesOverlap(arrival, departure, yearStart, yearEnd);
      })
      .map((employee) => {
        const arrival = clampDate(parseDate(employee.arrival_date), yearStart, yearEnd);
        const departure = clampDate(parseDate(employee.departure_date) || yearEnd, yearStart, yearEnd);
        const presenceStart = arrival;
        const presenceEnd = departure;
        const timeOffs = (employee.time_offs || [])
          .map((timeOff) => {
            const start = parseDate(timeOff.start_date);
            const end = parseDate(timeOff.end_date);
            if (!start || !end) return null;
            if (!rangesOverlap(start, end, yearStart, yearEnd)) return null;
            return {
              ...timeOff,
              start: clampDate(start, yearStart, yearEnd),
              end: clampDate(end, yearStart, yearEnd),
            };
          })
          .filter(Boolean);
        return {
          ...employee,
          statusGroup: getEmploymentStatus(employee),
          presenceStart,
          presenceEnd,
          timeOffSegments: timeOffs,
        };
      })
      .sort((left, right) => {
        const order = { current: 0, future: 1, past: 2 };
        const groupDiff = order[left.statusGroup] - order[right.statusGroup];
        if (groupDiff !== 0) return groupDiff;
        return String(left.name || "").localeCompare(String(right.name || ""));
      });
    return rows;
  }, [employees, yearEnd, yearStart]);

  const yearMonthTicks = useMemo(
    () =>
      Array.from({ length: 12 }, (_, monthIndex) => {
        const monthStart = new Date(year, monthIndex, 1);
        const nextMonth = monthIndex === 11 ? new Date(year + 1, 0, 1) : new Date(year, monthIndex + 1, 1);
        return {
          key: `${year}-${monthIndex + 1}`,
          label: monthStart.toLocaleDateString("fr-FR", { month: "short" }).replace(".", ""),
          left: percentBetweenDates(monthStart, yearStart, totalYearDays),
          width: (countInclusiveDays(monthStart, addDays(nextMonth, -1)) / totalYearDays) * 100,
        };
      }),
    [totalYearDays, year, yearStart]
  );

  const visibleRoadmapRows = useMemo(
    () =>
      roadmapRows
        .filter((employee) =>
          rangesOverlap(employee.presenceStart, employee.presenceEnd, viewStart, viewEnd)
        )
        .map((employee) => ({
          ...employee,
          visiblePresenceStart: maxDate(employee.presenceStart, viewStart),
          visiblePresenceEnd: minDate(employee.presenceEnd, viewEnd),
          visibleTimeOffSegments: employee.timeOffSegments
            .filter((timeOff) => rangesOverlap(timeOff.start, timeOff.end, viewStart, viewEnd))
            .map((timeOff) => ({
              ...timeOff,
              visibleStart: maxDate(timeOff.start, viewStart),
              visibleEnd: minDate(timeOff.end, viewEnd),
            })),
          visibleWorkingSegments: getWorkingDaySegments(employee, viewStart, viewEnd),
        })),
    [roadmapRows, viewEnd, viewStart]
  );

  const timelineTicks = useMemo(() => {
    if (totalViewDays <= 21) {
      return Array.from({ length: totalViewDays }, (_, offset) => {
        const currentDay = addDays(viewStart, offset);
        return {
          key: `day-${getDateKey(currentDay)}`,
          label: currentDay.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
          }),
          left: (offset / totalViewDays) * 100,
          width: (1 / totalViewDays) * 100,
        };
      });
    }

    if (totalViewDays <= 120) {
      const ticks = [];
      let cursor = startOfWeek(viewStart);
      while (cursor <= viewEnd) {
        const segmentStart = maxDate(cursor, viewStart);
        const segmentEnd = minDate(addDays(cursor, 6), viewEnd);
        ticks.push({
          key: `week-${getDateKey(segmentStart)}`,
          label: segmentStart.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
          }),
          left: percentBetweenDates(segmentStart, viewStart, totalViewDays),
          width: (countInclusiveDays(segmentStart, segmentEnd) / totalViewDays) * 100,
        });
        cursor = addDays(cursor, 7);
      }
      return ticks;
    }

    const ticks = [];
    let cursor = new Date(viewStart.getFullYear(), viewStart.getMonth(), 1);
    while (cursor <= viewEnd) {
      const segmentStart = maxDate(cursor, viewStart);
      const segmentEnd = minDate(endOfMonth(cursor.getFullYear(), cursor.getMonth()), viewEnd);
      ticks.push({
        key: `month-${cursor.getFullYear()}-${cursor.getMonth() + 1}`,
        label: cursor.toLocaleDateString("fr-FR", { month: "short" }).replace(".", ""),
        left: percentBetweenDates(segmentStart, viewStart, totalViewDays),
        width: (countInclusiveDays(segmentStart, segmentEnd) / totalViewDays) * 100,
      });
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }
    return ticks;
  }, [totalViewDays, viewEnd, viewStart]);

  const selectedRangeLabel = useMemo(() => {
    if (!selectedRange?.start || !selectedRange?.end) return "Aucune plage";
    return `${formatDate(selectedRange.start)} au ${formatDate(selectedRange.end)}`;
  }, [selectedRange]);

  const displayedRangeLabel = useMemo(
    () => `${formatDate(viewStart)} au ${formatDate(viewEnd)}`,
    [viewEnd, viewStart]
  );

  const rangeSummary = useMemo(() => {
    if (!selectedRange?.start || !selectedRange?.end) return null;
    const normalized = normalizeRange(selectedRange.start, selectedRange.end);
    const present = [];
    const arrivals = [];
    const departures = [];
    const timeOffs = [];
    roadmapRows.forEach((employee) => {
      if (rangesOverlap(employee.presenceStart, employee.presenceEnd, normalized.start, normalized.end)) {
        present.push(employee);
      }
      const arrival = parseDate(employee.arrival_date);
      if (arrival && arrival >= normalized.start && arrival <= normalized.end) {
        arrivals.push(employee);
      }
      const departure = parseDate(employee.departure_date);
      if (departure && departure >= normalized.start && departure <= normalized.end) {
        departures.push(employee);
      }
      employee.timeOffSegments.forEach((timeOff) => {
        if (rangesOverlap(timeOff.start, timeOff.end, normalized.start, normalized.end)) {
          timeOffs.push({
            employee,
            timeOff,
          });
        }
      });
    });
    return { present, arrivals, departures, timeOffs, range: normalized };
  }, [roadmapRows, selectedRange]);

  const selectionStyle = useMemo(() => {
    if (!selectedRange?.start || !selectedRange?.end) return null;
    const normalized = normalizeRange(selectedRange.start, selectedRange.end);
    const clippedStart = maxDate(normalized.start, viewStart);
    const clippedEnd = minDate(normalized.end, viewEnd);
    if (clippedStart > clippedEnd) return null;
    const startPct = percentBetweenDates(clippedStart, viewStart, totalViewDays);
    const endPct = percentBetweenDates(addDays(clippedEnd, 1), viewStart, totalViewDays);
    return {
      left: `${Math.max(0, startPct)}%`,
      width: `${Math.max(0.5, endPct - startPct)}%`,
    };
  }, [selectedRange, totalViewDays, viewEnd, viewStart]);

  const viewportStyle = useMemo(() => {
    const startPct = percentBetweenDates(viewStart, yearStart, totalYearDays);
    const endPct = percentBetweenDates(addDays(viewEnd, 1), yearStart, totalYearDays);
    return {
      left: `${Math.max(0, startPct)}%`,
      width: `${Math.max(0.5, endPct - startPct)}%`,
    };
  }, [totalYearDays, viewEnd, viewStart, yearStart]);

  const timelineMinWidth = useMemo(() => {
    if (totalViewDays <= 14) return Math.max(980, totalViewDays * 110);
    if (totalViewDays <= 31) return Math.max(980, totalViewDays * 58);
    if (totalViewDays <= 92) return Math.max(1100, totalViewDays * 24);
    return 980;
  }, [totalViewDays]);

  const showWorkingDaysOnTimeline = totalViewDays <= 120;

  const getDateFromClientX = useCallback((clientX, targetRef, rangeStart, rangeEnd) => {
      const rect = targetRef.current?.getBoundingClientRect();
      if (!rect) return null;
      const rawRatio = (clientX - rect.left) / rect.width;
      const ratio = Math.max(0, Math.min(1, rawRatio));
      const totalDays = countInclusiveDays(rangeStart, rangeEnd);
      const dayOffset = Math.floor(ratio * (totalDays - 1));
      return clampDate(addDays(rangeStart, dayOffset), rangeStart, rangeEnd);
    }, []);

  const beginRangeSelection = (event) => {
    const date = getDateFromClientX(event.clientX, roadmapRef, viewStart, viewEnd);
    if (!date) return;
    rangeDragRef.current = {
      anchor: date,
    };
    setSelectedRange({ start: date, end: date });
  };

  const beginViewportSelection = (event) => {
    const date = getDateFromClientX(event.clientX, roadmapOverviewRef, yearStart, yearEnd);
    if (!date) return;
    viewportDragRef.current = {
      anchor: date,
    };
    const nextRange = { start: date, end: date };
    setViewportRange(nextRange);
    setSelectedRange(nextRange);
  };

  const resetViewportRange = useCallback(() => {
    setViewportRange({ start: yearStart, end: yearEnd });
  }, [yearEnd, yearStart]);

  const zoomToSelectedRange = useCallback(() => {
    if (!selectedRange?.start || !selectedRange?.end) return;
    const normalized = normalizeRange(selectedRange.start, selectedRange.end);
    if (!normalized) return;
    setViewportRange(normalized);
  }, [selectedRange]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (viewportDragRef.current) {
        const date = getDateFromClientX(
          event.clientX,
          roadmapOverviewRef,
          yearStart,
          yearEnd
        );
        if (date) {
          const nextRange = normalizeRange(viewportDragRef.current.anchor, date);
          setViewportRange(nextRange);
          setSelectedRange(nextRange);
        }
      }
      if (!rangeDragRef.current) return;
      const date = getDateFromClientX(event.clientX, roadmapRef, viewStart, viewEnd);
      if (!date) return;
      setSelectedRange(normalizeRange(rangeDragRef.current.anchor, date));
    };
    const handleMouseUp = () => {
      viewportDragRef.current = null;
      rangeDragRef.current = null;
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [getDateFromClientX, viewEnd, viewStart, yearEnd, yearStart]);

  const syncRoadmapScroll = useCallback((source, target) => {
    if (!source || !target) return;
    if (Math.abs(target.scrollTop - source.scrollTop) < 1) return;
    roadmapScrollSyncRef.current = true;
    target.scrollTop = source.scrollTop;
  }, []);

  const handleRoadmapLabelsScroll = useCallback(
    (event) => {
      if (roadmapScrollSyncRef.current) {
        roadmapScrollSyncRef.current = false;
        return;
      }
      syncRoadmapScroll(event.currentTarget, roadmapRef.current);
    },
    [syncRoadmapScroll]
  );

  const handleRoadmapTimelineScroll = useCallback(
    (event) => {
      if (roadmapScrollSyncRef.current) {
        roadmapScrollSyncRef.current = false;
        return;
      }
      syncRoadmapScroll(event.currentTarget, roadmapLabelsRef.current);
    },
    [syncRoadmapScroll]
  );

  useEffect(() => {
    if (roadmapLabelsRef.current && roadmapRef.current) {
      roadmapLabelsRef.current.scrollTop = roadmapRef.current.scrollTop;
    }
  }, [visibleRoadmapRows.length, viewEnd, viewStart]);

  return (
    <div className="min-h-screen bg-slate-100 py-6 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      <div className="w-full space-y-6">
        <div className="rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-900 px-6 py-6 text-white shadow-xl">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-emerald-200">Equipe</div>
              <h1 className="mt-2 text-3xl font-semibold">Gestion des employes</h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-200">
                Contrats, jours travailles, absences et vision annuelle de la disponibilite de
                l'equipe.
              </p>
            </div>
            <button
              type="button"
              onClick={openCreateEmployee}
              className="inline-flex items-center justify-center rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-900 shadow hover:bg-slate-100"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Nouvel employe
            </button>
          </div>
        </div>

        {(error || success) && (
          <div
            className={`rounded-2xl px-4 py-3 text-sm shadow ${
              error
                ? "border border-rose-200 bg-rose-50 text-rose-700"
                : "border border-emerald-200 bg-emerald-50 text-emerald-700"
            }`}
          >
            {error || success}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl bg-white p-4 shadow">
            <div className="text-xs uppercase tracking-wide text-slate-400">Roster actif</div>
            <div className="mt-2 flex items-center gap-3">
              <Users className="h-5 w-5 text-slate-500" />
              <div className="text-2xl font-semibold text-slate-900">{summary.activeRoster}</div>
            </div>
          </div>
          <div className="rounded-2xl bg-white p-4 shadow">
            <div className="text-xs uppercase tracking-wide text-slate-400">
              Disponibles aujourd'hui
            </div>
            <div className="mt-2 flex items-center gap-3">
              <Clock3 className="h-5 w-5 text-emerald-600" />
              <div className="text-2xl font-semibold text-slate-900">
                {summary.availableToday}/{summary.scheduledToday}
              </div>
            </div>
          </div>
          <div className="rounded-2xl bg-white p-4 shadow">
            <div className="text-xs uppercase tracking-wide text-slate-400">Departs prevus</div>
            <div className="mt-2 flex items-center gap-3">
              <BriefcaseBusiness className="h-5 w-5 text-amber-600" />
              <div className="text-2xl font-semibold text-slate-900">
                {summary.upcomingDepartures}
              </div>
            </div>
          </div>
          <div className="rounded-2xl bg-white p-4 shadow">
            <div className="text-xs uppercase tracking-wide text-slate-400">
              Absences planifiees
            </div>
            <div className="mt-2 flex items-center gap-3">
              <Plane className="h-5 w-5 text-sky-600" />
              <div className="text-2xl font-semibold text-slate-900">{summary.openTimeOffs}</div>
            </div>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
          <div className="space-y-4">
            <div className="rounded-2xl bg-white p-4 shadow">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Equipe</div>
                  <div className="text-xs text-slate-500">
                    Liste compacte par statut de presence
                  </div>
                </div>
                <button
                  type="button"
                  onClick={fetchEmployees}
                  className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50"
                >
                  Rafraichir
                </button>
              </div>
              <div className="mt-4 max-h-[72vh] space-y-4 overflow-y-auto pr-1">
                {!loading &&
                  employeeSections.map((section) => (
                    <div key={section.key}>
                      <div className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                        <span>{section.label}</span>
                        <span>{section.items.length}</span>
                      </div>
                      <div className="space-y-2">
                        {section.items.map((employee) => {
                          const isSelected =
                            editorMode === "edit" &&
                            String(selectedEmployeeId) === String(employee.id);
                          return (
                            <button
                              key={employee.id}
                              type="button"
                              onClick={() => selectEmployee(employee)}
                              className={`w-full rounded-xl border px-3 py-2 text-left transition ${
                                isSelected
                                  ? "border-slate-900 bg-slate-900 text-white shadow"
                                  : "border-slate-200 bg-slate-50 text-slate-900 hover:border-slate-300 hover:bg-white"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="truncate text-sm font-medium">{employee.name}</div>
                                  <div
                                    className={`truncate text-[11px] ${
                                      isSelected ? "text-slate-300" : "text-slate-500"
                                    }`}
                                  >
                                    {contractTypeLabel(employee.contract_type)} •{" "}
                                    {formatWeeklyHours(employee.weekly_hours)} •{" "}
                                    {weekdayLabel(employee.weekly_work_days)}
                                  </div>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                  {employee.can_use_guttenberg && (
                                    <span
                                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                        isSelected
                                          ? "bg-blue-400/20 text-blue-200"
                                          : "bg-blue-50 text-blue-700"
                                      }`}
                                    >
                                      Guttenberg
                                    </span>
                                  )}
                                  {employee.can_edit_offers && (
                                    <span
                                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                        isSelected
                                          ? "bg-emerald-400/20 text-emerald-200"
                                          : "bg-emerald-50 text-emerald-700"
                                      }`}
                                    >
                                      Offres
                                    </span>
                                  )}
                                  {employee.is_admin && (
                                    <Shield
                                      className={`h-4 w-4 ${
                                        isSelected ? "text-emerald-300" : "text-emerald-600"
                                      }`}
                                    />
                                  )}
                                </div>
                              </div>
                              <div
                                className={`mt-2 flex items-center justify-between text-[11px] ${
                                  isSelected ? "text-slate-300" : "text-slate-500"
                                }`}
                              >
                                <span>{formatDate(employee.arrival_date)}</span>
                                <span>{employee.departure_date ? formatDate(employee.departure_date) : "Sans fin"}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                {!loading && employees.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    Aucun employe enregistre.
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl bg-white p-5 shadow">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {editorMode === "create" ? "Nouvel employe" : selectedEmployee?.name || "Edition"}
                  </div>
                  <div className="text-xs text-slate-500">
                    Gere le contrat, la base horaire hebdo, les jours travailles et le statut global.
                  </div>
                </div>
                {editorMode === "edit" && selectedEmployee && (
                  <button
                    type="button"
                    onClick={handleDeleteEmployee}
                    className="inline-flex items-center rounded-full border border-rose-200 px-3 py-2 text-xs text-rose-600 hover:bg-rose-50"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Supprimer
                  </button>
                )}
              </div>

              <form onSubmit={handleEmployeeSubmit} className="space-y-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                      Nom
                    </label>
                    <input
                      type="text"
                      value={employeeForm.name}
                      onChange={(event) => updateEmployeeForm("name", event.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                      Email
                    </label>
                    <input
                      type="email"
                      value={employeeForm.email}
                      onChange={(event) => updateEmployeeForm("email", event.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                      Type de contrat
                    </label>
                    <select
                      value={employeeForm.contract_type}
                      onChange={(event) => updateEmployeeForm("contract_type", event.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    >
                      {CONTRACT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                      Taux de production
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={employeeForm.production_rate}
                      onChange={(event) =>
                        updateEmployeeForm(
                          "production_rate",
                          Math.max(0, Math.min(100, Number(event.target.value || 0)))
                        )
                      }
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                      Base hebdo (h)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="80"
                      step="0.5"
                      value={employeeForm.weekly_hours}
                      onChange={(event) =>
                        updateEmployeeForm(
                          "weekly_hours",
                          Math.max(1, Math.min(80, Number(event.target.value || 35)))
                        )
                      }
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                      Date d'arrivee
                    </label>
                    <input
                      type="date"
                      value={employeeForm.arrival_date}
                      onChange={(event) => updateEmployeeForm("arrival_date", event.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                      Date de fin prevue
                    </label>
                    <input
                      type="date"
                      value={employeeForm.departure_date}
                      onChange={(event) => updateEmployeeForm("departure_date", event.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Jours travailles
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {WEEKDAY_OPTIONS.map((option) => {
                      const enabled = (employeeForm.weekly_work_days || []).includes(option.value);
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => toggleWorkingDay(option.value)}
                          className={`rounded-full px-3 py-2 text-sm ${
                            enabled
                              ? "bg-slate-900 text-white"
                              : "border border-slate-200 bg-white text-slate-600"
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={Boolean(employeeForm.is_active)}
                      onChange={(event) => updateEmployeeForm("is_active", event.target.checked)}
                    />
                    Employe actif
                  </label>
                  {isSuperAdmin && (
                    <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={Boolean(employeeForm.is_admin)}
                        onChange={(event) => updateEmployeeForm("is_admin", event.target.checked)}
                      />
                      Administrateur
                    </label>
                  )}
                  <label className="flex items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50/50 px-4 py-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={Boolean(employeeForm.can_use_guttenberg)}
                      onChange={(event) =>
                        updateEmployeeForm("can_use_guttenberg", event.target.checked)
                      }
                    />
                    Autoriser l'utilisation de Guttenberg
                  </label>
                  <label className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/50 px-4 py-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={Boolean(employeeForm.can_edit_offers)}
                      onChange={(event) =>
                        updateEmployeeForm("can_edit_offers", event.target.checked)
                      }
                    />
                    Autoriser la modification des chiffrages et offres
                  </label>
                  <label className="flex items-center gap-3 rounded-2xl border border-indigo-200 bg-indigo-50/50 px-4 py-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={Boolean(employeeForm.can_use_home)}
                      onChange={(event) =>
                        updateEmployeeForm("can_use_home", event.target.checked)
                      }
                    />
                    Page d'accueil personnalisée (préparée chaque matin par Nexus)
                  </label>
                  <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={Boolean(employeeForm.can_view_pilotage)}
                      onChange={(event) =>
                        updateEmployeeForm("can_view_pilotage", event.target.checked)
                      }
                    />
                    Accès au pilotage, page et outils agents (inclus pour les administrateurs)
                  </label>
                  <label className="flex items-center gap-3 rounded-2xl border border-indigo-200 bg-indigo-50/50 px-4 py-3 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={Boolean(employeeForm.can_use_new_project)}
                      onChange={(event) =>
                        updateEmployeeForm("can_use_new_project", event.target.checked)
                      }
                    />
                    Nouvelle fiche projet (aperçu, onglets et nouvelle roadmap)
                  </label>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    className="inline-flex items-center rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {editorMode === "create" ? "Ajouter l'employe" : "Enregistrer"}
                  </button>
                  {editorMode === "edit" && (
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedEmployee) {
                          setEmployeeForm(toEmployeeForm(selectedEmployee));
                        }
                        setTimeOffForm(EMPTY_TIME_OFF_FORM);
                      }}
                      className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50"
                    >
                      Annuler les modifications
                    </button>
                  )}
                </div>
              </form>
            </div>

            <div className="rounded-2xl bg-white p-5 shadow">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Absences et conges</div>
                  <div className="text-xs text-slate-500">
                    {selectedEmployee
                      ? `Gestion des absences de ${selectedEmployee.name}`
                      : "Selectionne un employe pour gerer ses absences"}
                  </div>
                </div>
                {selectedRange && selectedEmployee && (
                  <button
                    type="button"
                    onClick={() =>
                      setTimeOffForm((prev) => ({
                        ...prev,
                        start_date: toDateInput(selectedRange.start),
                        end_date: toDateInput(selectedRange.end),
                      }))
                    }
                    className="rounded-full border border-sky-200 px-3 py-2 text-xs text-sky-700 hover:bg-sky-50"
                  >
                    Utiliser la plage selectionnee
                  </button>
                )}
              </div>

              {selectedEmployee ? (
                <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                  <div className="space-y-3">
                    {(selectedEmployee.time_offs || []).length > 0 ? (
                      selectedEmployee.time_offs
                        .slice()
                        .sort((left, right) =>
                          String(left.start_date).localeCompare(String(right.start_date))
                        )
                        .map((timeOff) => (
                          <div
                            key={timeOff.id}
                            className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:flex-row md:items-center md:justify-between"
                          >
                            <div>
                              <div className="text-sm font-medium text-slate-900">
                                {timeOffTypeLabel(timeOff.leave_type)}
                                {timeOff.label ? ` • ${timeOff.label}` : ""}
                              </div>
                              <div className="mt-1 text-xs text-slate-500">
                                {formatDate(timeOff.start_date)} au {formatDate(timeOff.end_date)}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => editTimeOff(timeOff)}
                                className="rounded-full border border-slate-200 px-3 py-2 text-xs text-slate-600 hover:bg-white"
                              >
                                Modifier
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteTimeOff(timeOff.id)}
                                className="rounded-full border border-rose-200 px-3 py-2 text-xs text-rose-600 hover:bg-rose-50"
                              >
                                Supprimer
                              </button>
                            </div>
                          </div>
                        ))
                    ) : (
                      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                        Aucune absence enregistree.
                      </div>
                    )}
                  </div>

                  <form
                    onSubmit={handleTimeOffSubmit}
                    className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="text-sm font-medium text-slate-900">
                      {timeOffForm.id ? "Modifier une absence" : "Ajouter une absence"}
                    </div>
                    <div className="mt-4 space-y-3">
                      <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                          Type
                        </label>
                        <select
                          value={timeOffForm.leave_type}
                          onChange={(event) =>
                            setTimeOffForm((prev) => ({
                              ...prev,
                              leave_type: event.target.value,
                            }))
                          }
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        >
                          {TIME_OFF_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                          Libelle
                        </label>
                        <input
                          type="text"
                          value={timeOffForm.label}
                          onChange={(event) =>
                            setTimeOffForm((prev) => ({ ...prev, label: event.target.value }))
                          }
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                          placeholder="Ex: fermeture d'ete"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                          Debut
                        </label>
                        <input
                          type="date"
                          value={timeOffForm.start_date}
                          onChange={(event) =>
                            setTimeOffForm((prev) => ({
                              ...prev,
                              start_date: event.target.value,
                            }))
                          }
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                          Fin
                        </label>
                        <input
                          type="date"
                          value={timeOffForm.end_date}
                          onChange={(event) =>
                            setTimeOffForm((prev) => ({ ...prev, end_date: event.target.value }))
                          }
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                          required
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="submit"
                        className="rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                      >
                        {timeOffForm.id ? "Mettre a jour" : "Ajouter"}
                      </button>
                      {timeOffForm.id && (
                        <button
                          type="button"
                          onClick={() => setTimeOffForm(EMPTY_TIME_OFF_FORM)}
                          className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-white"
                        >
                          Reinitialiser
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  Selectionne un employe pour ajouter ou modifier ses absences.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-white p-5 shadow">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-900">
                Roadmap annuelle d'equipe
              </div>
              <div className="text-xs text-slate-500">
                Glisse sur la timeline pour selectionner une plage. Les lignes affichent
                uniquement les employes presents au moins une fois dans l'annee.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setYear((prev) => prev - 1)}
                className="rounded-full border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-900">
                {year}
              </div>
              <button
                type="button"
                onClick={() => setYear((prev) => prev + 1)}
                className="rounded-full border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400">
                    Plage affichee
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-900">
                    {displayedRangeLabel}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    Glisse sur le bandeau annuel pour zoomer, puis sur la roadmap pour affiner une
                    selection.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-800">
                    Presence
                  </span>
                  <span className="rounded-full bg-sky-100 px-3 py-1 text-sky-800">
                    Absence
                  </span>
                  <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">
                    Jours travailles
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400">
                    Selection active
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-900">{selectedRangeLabel}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={zoomToSelectedRange}
                    className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100"
                  >
                    Zoomer sur la selection
                  </button>
                  <button
                    type="button"
                    onClick={resetViewportRange}
                    className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100"
                  >
                    Voir toute l'annee
                  </button>
                </div>
              </div>

              <div
                ref={roadmapOverviewRef}
                className="relative h-16 cursor-crosshair overflow-hidden rounded-2xl border border-slate-200 bg-white"
                onMouseDown={beginViewportSelection}
              >
                {yearMonthTicks.map((month) => (
                  <div
                    key={`overview-${month.key}`}
                    className="pointer-events-none absolute bottom-0 top-0 border-r border-slate-100 px-2 py-2 text-[11px] font-medium uppercase tracking-wide text-slate-400"
                    style={{ left: `${month.left}%`, width: `${month.width}%` }}
                  >
                    {month.label}
                  </div>
                ))}
                <div
                  className="pointer-events-none absolute inset-y-2 rounded-xl bg-slate-900/10 ring-1 ring-slate-300"
                  style={viewportStyle}
                />
              </div>
            </div>
          </div>

          <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200">
            <div className="grid grid-cols-[220px_minmax(0,1fr)] xl:grid-cols-[240px_minmax(0,1fr)]">
              <div className="border-r border-slate-200 bg-slate-50">
                <div className="flex h-14 items-center border-b border-slate-200 px-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Employes
                </div>
                <div
                  ref={roadmapLabelsRef}
                  className="max-h-[620px] overflow-y-auto"
                  onScroll={handleRoadmapLabelsScroll}
                >
                  {visibleRoadmapRows.map((employee) => (
                    <button
                      key={`roadmap-label-${employee.id}`}
                      type="button"
                      onClick={() => selectEmployee(employee)}
                      className={`flex h-14 w-full items-center justify-between border-b border-slate-100 px-4 text-left ${
                        String(selectedEmployeeId) === String(employee.id)
                          ? "bg-slate-900 text-white"
                          : "bg-white text-slate-900 hover:bg-slate-50"
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{employee.name}</div>
                        <div
                          className={`truncate text-[11px] ${
                            String(selectedEmployeeId) === String(employee.id)
                              ? "text-slate-300"
                              : "text-slate-500"
                          }`}
                        >
                          {contractTypeLabel(employee.contract_type)} • {formatWeeklyHours(employee.weekly_hours)} • {weekdayLabel(employee.weekly_work_days)}
                        </div>
                      </div>
                      <div
                        className={`rounded-full px-2 py-1 text-[10px] uppercase ${
                          employee.statusGroup === "current"
                            ? "bg-emerald-100 text-emerald-800"
                            : employee.statusGroup === "future"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-slate-200 text-slate-600"
                        }`}
                      >
                        {employee.statusGroup === "current"
                          ? "En poste"
                          : employee.statusGroup === "future"
                          ? "A venir"
                          : "Sorti"}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto bg-white">
                <div style={{ minWidth: `${timelineMinWidth}px` }}>
                  <div className="relative h-14 border-b border-slate-200 bg-slate-50">
                    {timelineTicks.map((tick) => (
                      <div
                        key={tick.key}
                        className="absolute bottom-0 top-0 border-r border-slate-200 px-2 py-3 text-xs font-medium uppercase tracking-wide text-slate-500"
                        style={{ left: `${tick.left}%`, width: `${tick.width}%` }}
                      >
                        {tick.label}
                      </div>
                    ))}
                  </div>
                  <div
                    ref={roadmapRef}
                    className="relative max-h-[620px] overflow-y-auto"
                    onMouseDown={beginRangeSelection}
                    onScroll={handleRoadmapTimelineScroll}
                  >
                    {selectionStyle && (
                      <div
                        className="pointer-events-none absolute bottom-0 top-0 z-10 bg-sky-200/35 ring-1 ring-sky-300"
                        style={selectionStyle}
                      />
                    )}
                    {timelineTicks.map((tick) => (
                      <div
                        key={`grid-${tick.key}`}
                        className="pointer-events-none absolute bottom-0 top-0 border-r border-slate-100"
                        style={{ left: `${tick.left}%`, width: `${tick.width}%` }}
                      />
                    ))}
                    <div className="relative z-20">
                      {visibleRoadmapRows.map((employee) => {
                        const startPct = percentBetweenDates(
                          employee.visiblePresenceStart,
                          viewStart,
                          totalViewDays
                        );
                        const endPct = percentBetweenDates(
                          addDays(employee.visiblePresenceEnd, 1),
                          viewStart,
                          totalViewDays
                        );
                        return (
                          <div key={`roadmap-row-${employee.id}`} className="relative h-14 border-b border-slate-100">
                            <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-slate-100" />
                            <div
                              className="absolute top-1/2 h-6 -translate-y-1/2 rounded-full bg-emerald-400/85 shadow-sm"
                              style={{
                                left: `${startPct}%`,
                                width: `${Math.max(1.2, endPct - startPct)}%`,
                              }}
                              title={`${employee.name} • ${formatDate(employee.presenceStart)} au ${formatDate(employee.presenceEnd)}`}
                            />
                            {showWorkingDaysOnTimeline &&
                              employee.visibleWorkingSegments.map((segment, index) => {
                                const workingStart = percentBetweenDates(
                                  segment.start,
                                  viewStart,
                                  totalViewDays
                                );
                                const workingEnd = percentBetweenDates(
                                  addDays(segment.end, 1),
                                  viewStart,
                                  totalViewDays
                                );
                                return (
                                  <div
                                    key={`working-${employee.id}-${index}`}
                                    className="absolute top-1/2 h-2 -translate-y-1/2 rounded-full bg-amber-400/90"
                                    style={{
                                      left: `${workingStart}%`,
                                      width: `${Math.max(0.6, workingEnd - workingStart)}%`,
                                    }}
                                    title={`${employee.name} â€¢ Base hebdo : ${formatWeeklyHours(employee.weekly_hours)} â€¢ Jours travailles : ${weekdayLabel(employee.weekly_work_days)}`}
                                  />
                                );
                              })}
                            {employee.visibleTimeOffSegments.map((timeOff) => {
                              const timeOffStart = percentBetweenDates(
                                timeOff.visibleStart,
                                viewStart,
                                totalViewDays
                              );
                              const timeOffEnd = percentBetweenDates(
                                addDays(timeOff.visibleEnd, 1),
                                viewStart,
                                totalViewDays
                              );
                              return (
                                <div
                                  key={`timeoff-${employee.id}-${timeOff.id}`}
                                  className="absolute top-1/2 h-4 -translate-y-1/2 rounded-full bg-sky-500/90"
                                  style={{
                                    left: `${timeOffStart}%`,
                                    width: `${Math.max(0.8, timeOffEnd - timeOffStart)}%`,
                                  }}
                                  title={`${employee.name} • ${timeOffTypeLabel(timeOff.leave_type)} • ${formatDate(timeOff.start)} au ${formatDate(timeOff.end)}`}
                                />
                              );
                            })}
                          </div>
                        );
                      })}
                      {visibleRoadmapRows.length === 0 && (
                        <div className="flex h-48 items-center justify-center text-sm text-slate-500">
                          Aucun employe present sur la plage affichee.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-sm font-semibold text-slate-900">
              Detail de la plage {selectedRangeLabel}
            </div>
            {rangeSummary ? (
              <div className="mt-4 grid gap-4 lg:grid-cols-4">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Presents sur la plage
                  </div>
                  <div className="mt-2 text-sm text-slate-700">
                    {rangeSummary.present.length
                      ? rangeSummary.present.map((employee) => employee.name).join(", ")
                      : "Personne"}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Arrivees
                  </div>
                  <div className="mt-2 text-sm text-slate-700">
                    {rangeSummary.arrivals.length
                      ? rangeSummary.arrivals.map((employee) => employee.name).join(", ")
                      : "Aucune"}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Departs
                  </div>
                  <div className="mt-2 text-sm text-slate-700">
                    {rangeSummary.departures.length
                      ? rangeSummary.departures.map((employee) => employee.name).join(", ")
                      : "Aucun"}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Absences recouvertes
                  </div>
                  <div className="mt-2 text-sm text-slate-700">
                    {rangeSummary.timeOffs.length
                      ? rangeSummary.timeOffs
                          .map(
                            ({ employee, timeOff }) =>
                              `${employee.name} (${timeOffTypeLabel(timeOff.leave_type)})`
                          )
                          .join(", ")
                      : "Aucune"}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-3 text-sm text-slate-500">Aucune plage selectionnee.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeManagement;
